/**
 * Quién cubre cada tramo de la semana.
 *
 * La pregunta del dueño no es «¿qué horario tiene Elena?»: es «¿hay alguien
 * a las 13 del jueves?». Cada uno va en su columna; a la misma hora se ve
 * quién cubre y quién no.
 */
import { textoTramos, tramosDeFranja } from './horarios-tramos'
import { cierreEnDia, type PeriodoCierre } from '../routes/calendario-cerrados'
import {
    horarioDeVariosDias,
    jornadaDe,
    restarIntervalos,
    type HorarioVisible,
    type IntervaloMin,
    type PlanSemanal,
} from '../routes/calendario-geometria'

export interface ProfesionalPlan {
    id: number
    plan: PlanSemanal
}

export interface BandaCobertura {
    profesionalId: number
    desdeMin: number
    hastaMin: number
    tramo: number
}

export function jornadaCobrada(
    plan: PlanSemanal,
    profesionalId: number,
    dia: string,
    vacaciones: readonly PeriodoCierre[],
): IntervaloMin[] {
    const jornada = jornadaDe(plan, dia)
    if (jornada.length === 0) return []
    const quita: IntervaloMin[] = []
    for (const v of vacaciones) {
        if (v.profesionalId !== profesionalId) continue
        const trozo = cierreEnDia(v.inicio, v.fin, dia)
        if (trozo) quita.push(trozo)
    }
    return restarIntervalos(jornada, quita)
}

export function bandasDelDia(
    profesionales: readonly ProfesionalPlan[],
    dia: string,
    vacaciones: readonly PeriodoCierre[],
): BandaCobertura[] {
    const out: BandaCobertura[] = []
    for (const p of profesionales) {
        let tramo = 0
        for (const t of jornadaCobrada(p.plan, p.id, dia, vacaciones)) {
            out.push({ profesionalId: p.id, desdeMin: t.desdeMin, hastaMin: t.hastaMin, tramo: tramo++ })
        }
    }
    return out
}

/** Minutos del día en los que no queda nadie, dentro del horario visible. */
export function huecosDelDia(horario: HorarioVisible, bandas: readonly BandaCobertura[]): IntervaloMin[] {
    const cubierto = bandas.map((b) => ({ desdeMin: b.desdeMin, hastaMin: b.hastaMin }))
    return restarIntervalos([{ desdeMin: horario.desdeMin, hastaMin: horario.hastaMin }], cubierto)
}

export function coberturaEn(bandas: readonly Pick<BandaCobertura, 'desdeMin' | 'hastaMin'>[], min: number): number {
    let n = 0
    for (const b of bandas) {
        if (min >= b.desdeMin && min < b.hastaMin) n += 1
    }
    return n
}

/** Los días de la semana en los que trabaja alguien. Si no queda ninguno, no se toca. */
export function diasConAlguien(
    profesionales: readonly ProfesionalPlan[],
    dias: readonly string[],
    vacaciones: readonly PeriodoCierre[],
): string[] {
    const con = dias.filter((d) => bandasDelDia(profesionales, d, vacaciones).length > 0)
    return con.length > 0 ? con : [...dias]
}

export function horarioDeLaSemana(profesionales: readonly ProfesionalPlan[], dias: readonly string[]): HorarioVisible {
    return horarioDeVariosDias(
        profesionales.map((p) => p.plan),
        [...dias],
    )
}

const CLAVE_DIA = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
const CORTO = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'] as const

/** Una línea: «Lun–Vie 09:00–18:00 · Sáb 10:00–14:00 · Dom cerrado». */
export function resumenPlan(plan: PlanSemanal): string {
    const filas: { desde: number; hasta: number; texto: string }[] = []
    for (let i = 0; i < 7; i++) {
        const f = plan[CLAVE_DIA[i]!]
        const texto = f ? textoTramos(tramosDeFranja(f)) : 'cerrado'
        const ultimo = filas[filas.length - 1]
        if (ultimo && ultimo.texto === texto && ultimo.hasta === i - 1) {
            ultimo.hasta = i
        } else {
            filas.push({ desde: i, hasta: i, texto })
        }
    }
    return filas
        .map((g) => {
            const dias = g.desde === g.hasta ? CORTO[g.desde] : `${CORTO[g.desde]}–${CORTO[g.hasta]}`
            return `${dias} ${g.texto}`
        })
        .join(' · ')
}
